import { Button, Flex, Input, Modal, ScrollArea, Text } from "@mantine/core";
import { MIME_TYPES } from "@mantine/dropzone";
import { isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { CSVLink } from "react-csv";

import { useLastImportDate } from "../../api/hooks/import/useLastImportDate.ts";
import Dropzone from "../../components/Dropzone/Dropzone.tsx";
import {
  FileResult,
  ImportResult,
  ImportType,
} from "../../graphql/generated.ts";
import { toLocalDate } from "../../utils/date.ts";
import SvgInfo from "../Icons/Info.tsx";
import SvgUpload from "../Icons/Upload.tsx";
import Preloader from "../Preloader/Preloader.tsx";

interface Props {
  loading: boolean;
  options: {
    title: string;
    type: ImportType;
    information?: ReactNode;
    warning?: ReactNode;
    buttonText?: string;
    buttonVariant?: string;
    csvTemplateFilename: string;
    csvTemplateHeaders: { key: string; label: string }[];
  };
  onImport: (input: { file: string }) => Promise<ImportResult | undefined>;
  onRefresh?: () => void;
}

export const ImportModal = ({
  options,
  loading,
  onImport,
  onRefresh,
}: Props) => {
  const [
    { data: lastImportDateData, fetching: loadingLastImportDate },
    refreshLastImportDate,
  ] = useLastImportDate({
    type: options.type,
  });
  const lastImportDate = lastImportDateData?.lastImportDate;

  const [modalOpened, { open: modalOpen, close: modalClose }] =
    useDisclosure(false);

  const [errors, setErrors] = useState<
    | {
        formErrors: string[];
        fieldErrors: Record<string, string[]>;
      }
    | null
    | undefined
  >();

  const form = useForm<{ file: FileResult }>({
    validateInputOnBlur: true,
    validate: {
      file: isNotEmpty("Field is required"),
    },
  });

  const handleUploadFile = (files: FileResult[]) => {
    setErrors(null);
    form.setValues({ file: files[0] });
  };

  const handleRemoveFile = () => {
    setErrors(null);
    form.setValues({ file: undefined });
  };

  const handleOpen = useCallback(() => {
    refreshLastImportDate();
    modalOpen();
  }, [modalOpen, refreshLastImportDate]);

  const handleClose = useCallback(() => {
    modalClose();
    form.reset();
    setErrors(null);
  }, [form, modalClose]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid()) {
      return;
    }
    setErrors(null);
    onImport({ file: form.values.file.filePath }).then((data) => {
      if (data?.status === "success") {
        handleClose();
        onRefresh && onRefresh();
        return;
      }
      setErrors(data?.errors);
    });
  }, [form, handleClose, onImport, onRefresh]);

  const renderedErrors = useMemo(() => {
    if (!errors) {
      return null;
    }
    return (
      <Flex direction="column" gap={6}>
        {errors.formErrors.map((e, index) => (
          <Text key={`formError-${index}`} variant="error">
            {e}
          </Text>
        ))}
        {Object.keys(errors.fieldErrors).map((eField, index) => (
          <Flex direction="column" key={`fieldError-${index}`}>
            <Text variant="error">Row {Number(eField) + 1}:</Text>
            {errors.fieldErrors[eField].map((error, eIndex) => (
              <Text
                key={`fieldError-${index}-${eIndex}`}
                variant="error"
                ml={6}
              >
                {error}
              </Text>
            ))}
          </Flex>
        ))}
      </Flex>
    );
  }, [errors]);

  return (
    <>
      <Button
        size="md"
        variant={options.buttonVariant || "outlineGolden"}
        uppercase
        leftIcon={<SvgUpload />}
        onClick={handleOpen}
      >
        {options.buttonText ?? "upload"}
      </Button>
      {modalOpened && (
        <Modal
          size="lg"
          opened={true}
          title={options.title}
          closeButtonProps={{
            size: 24,
            iconSize: 24,
          }}
          scrollAreaComponent={ScrollArea.Autosize}
          onClose={handleClose}
        >
          {loadingLastImportDate && <Preloader loading={true} />}
          <Flex direction="column" gap={10}>
            <Text color="dimmed">
              Please ensure that all columns strictly follow the format in the
              provided .csv template.
            </Text>
            {options.information && (
              <Flex gap={6} direction="column">
                <Flex align="center" gap={6}>
                  <SvgInfo />{" "}
                  <Text weight={500} color="dimmed">
                    Important Information
                  </Text>
                </Flex>
                <Text color="dimmed">{options.information}</Text>
              </Flex>
            )}
            <Text color="dimmed">
              Any mismatch may result in an import error and failure to upload
              the data correctly.
            </Text>
            {options.warning && (
              <Flex gap={6} direction="column">
                <Flex align="center" gap={6}>
                  <SvgInfo width={24} />{" "}
                  <Text weight={500} color="dimmed">
                    Warning
                  </Text>
                </Flex>
                <Text color="dimmed">{options.warning}</Text>
              </Flex>
            )}
            <Flex align="center" justify="center">
              <CSVLink
                filename={options.csvTemplateFilename}
                data={[]}
                headers={options.csvTemplateHeaders}
              >
                <Button size="md" variant="outlineGolden">
                  Download .csv template here
                </Button>
              </CSVLink>
            </Flex>
            <Flex gap={12} direction="column">
              <Input.Wrapper size="m" w="100%">
                <Dropzone
                  maxWidth="100%"
                  multiple={false}
                  value={form.values.file ? [form.values.file] : []}
                  accept={[MIME_TYPES.csv]}
                  subLabel="Only csv files up to 5MB are allowed"
                  onUploadSuccess={handleUploadFile}
                  onRemove={handleRemoveFile}
                />
              </Input.Wrapper>
            </Flex>
            {renderedErrors}
            {lastImportDate && (
              <Text>Last Import Date: {toLocalDate(lastImportDate)}</Text>
            )}
            <Flex gap={20} justify="end">
              <Button
                variant="outlineBlue"
                uppercase
                type="reset"
                onClick={handleClose}
              >
                cancel
              </Button>
              <Button
                variant="filledBlue"
                uppercase
                type="submit"
                disabled={!form.isValid()}
                loading={loading}
                onClick={handleSubmit}
              >
                upload
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </>
  );
};
